import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { sentenceInfoService, SentenceInfoRow } from '../services/apiService';

const AdminSentenceInfo: React.FC = () => {
  const { token } = useAuth();

  const [sHead, setSHead] = useState('');
  const [sDetail, setSDetail] = useState('');
  const [sDate, setSDate] = useState('');
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [items, setItems] = useState<SentenceInfoRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      setLoading(true);
      const res = await sentenceInfoService.list(token, 80);
      const data = res.data as { success?: boolean; items?: SentenceInfoRow[] } | undefined;
      if (data?.items) setItems(data.items);
    } catch {
      setMessage({ type: 'err', text: 'Liste yüklenemedi.' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);
    if (!token) return;
    if (!sHead.trim() && !sDetail.trim()) {
      setMessage({ type: 'err', text: 'Başlık veya detaydan en az biri dolu olmalı.' });
      return;
    }
    try {
      setSaving(true);
      const res = await sentenceInfoService.create(token, {
        sHead: sHead.trim() || undefined,
        sDetail: sDetail.trim() || undefined,
        sDate: sDate ? new Date(sDate).toISOString() : undefined,
      });
      const data = res.data as { success?: boolean; id?: number } | undefined;
      if (data?.success) {
        setMessage({ type: 'ok', text: `Kayıt eklendi (Id: ${data.id ?? '—'})` });
        setSHead('');
        setSDetail('');
        setSDate('');
        await load();
      } else {
        setMessage({ type: 'err', text: 'Kayıt eklenemedi.' });
      }
    } catch {
      setMessage({ type: 'err', text: 'Kayıt eklenirken hata oluştu.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!token) return;
    if (!window.confirm(`Bu kayıt silinsin mi? (Id: ${id})`)) return;
    setMessage(null);
    try {
      setDeletingId(id);
      const res = await sentenceInfoService.delete(token, id);
      const data = res.data as { success?: boolean } | undefined;
      if (data?.success) {
        setMessage({ type: 'ok', text: `Kayıt silindi (Id: ${id})` });
        await load();
      } else {
        setMessage({ type: 'err', text: 'Silinemedi.' });
      }
    } catch {
      setMessage({ type: 'err', text: 'Silme sırasında hata oluştu.' });
    } finally {
      setDeletingId(null);
    }
  };

  const formatState = (v: string | number | null | undefined) => {
    if (v === null || v === undefined || v === '') return '—';
    if (v === 1 || v === '1') return 'Planlandı';
    return String(v);
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Afirmasyonlar</h1>
        <p className="mt-1 text-sm text-gray-600">Olumlu cümle / afirmasyon havuzu (SentenceInfo)</p>
      </div>

      <div className="mb-8 rounded-xl border border-gray-100 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">Yeni kayıt</h2>
        {message && (
          <div
            className={`mb-4 rounded-lg px-4 py-3 text-sm ${
              message.type === 'ok'
                ? 'border border-green-200 bg-green-50 text-green-800'
                : 'border border-red-200 bg-red-50 text-red-800'
            }`}
          >
            {message.text}
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">S_Head (başlık)</label>
            <input
              type="text"
              value={sHead}
              onChange={(e) => setSHead(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-purple-500"
              placeholder="Kısa başlık"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">S_Detail (metin)</label>
            <textarea
              value={sDetail}
              onChange={(e) => setSDetail(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-purple-500"
              placeholder="Cümle / afirmasyon metni"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">S_Date (isteğe bağlı)</label>
            <input
              type="datetime-local"
              value={sDate}
              onChange={(e) => setSDate(e.target.value)}
              className="w-full max-w-md rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:ring-2 focus:ring-purple-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Boş bırakılırsa sunucu UTC zamanı kullanılır. Durum sunucuda otomatik «Planlandı» yazılır.
            </p>
          </div>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-purple-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-purple-700 disabled:bg-gray-400"
          >
            {saving ? 'Kaydediliyor…' : 'Tabloya yaz'}
          </button>
        </form>
      </div>

      <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h2 className="text-lg font-semibold text-gray-900">Son kayıtlar</h2>
          <button
            type="button"
            onClick={() => void load()}
            className="text-sm font-medium text-purple-600 hover:text-purple-800"
          >
            Yenile
          </button>
        </div>
        {loading ? (
          <div className="p-12 text-center text-gray-500">Yükleniyor…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-gray-500">Henüz kayıt yok veya liste boş.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-gray-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Id</th>
                  <th className="px-4 py-3 font-medium">S_Head</th>
                  <th className="px-4 py-3 font-medium">S_Detail</th>
                  <th className="px-4 py-3 font-medium">S_Date</th>
                  <th className="px-4 py-3 font-medium">Durum</th>
                  <th className="w-24 px-4 py-3 font-medium">İşlem</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50/80">
                    <td className="whitespace-nowrap px-4 py-3 text-gray-700">{row.id}</td>
                    <td className="max-w-[180px] truncate px-4 py-3 text-gray-800" title={row.sHead ?? ''}>
                      {row.sHead ?? '—'}
                    </td>
                    <td className="max-w-md truncate px-4 py-3 text-gray-600" title={row.sDetail ?? ''}>
                      {row.sDetail ?? '—'}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                      {row.sDate != null ? String(row.sDate) : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-800">{formatState(row.sState)}</td>
                    <td className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => void handleDelete(row.id)}
                        disabled={deletingId === row.id}
                        className="text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                      >
                        {deletingId === row.id ? 'Siliniyor…' : 'Sil'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSentenceInfo;
